/**
 * This file contains helper functions for finding the most similar saved reply to a comment
 * using cosine similarity.
 *
 * We have used the cosine similarity library to calculate the similarity between the comment and the saved replies,
 * porter2 library for stemming and stopwords-iso library for stopwords.
 * We have used these libraries because they are lighter in size and faster in execution.
 *
 */

import cosineSimilarity from 'cos-similarity'
import { stem } from 'porter2'
import stopwords from 'stopwords-iso'

import type { FragmentOf } from '~publish/graphql'

import type { EngagementReplySuggestions_SavedReply } from '../components/EngagementReplySuggestions/EngagementReplySuggestions'

/**
 * The saved-reply shape this similarity helper operates on: the colocated
 * `EngagementReplySuggestions` leaf fragment, which is also what the
 * most-similar chip renders. Reads only `content.text` but returns a whole
 * element, so the type must carry the full fragment (not just `content`).
 */
type SimilaritySavedReply = FragmentOf<
  typeof EngagementReplySuggestions_SavedReply
>

// Load English stopwords into a Set for fast lookup
const stopWords = new Set(stopwords.en)

/**
 * Tokenization and stemming function.
 * It removes stop words and returns the stemmed tokens.
 *
 * @param text - The text to process
 * @returns The tokenized and stemmed text without stop words
 */
function tokenizeAndStem(text: string): string[] {
  return (
    text
      .toLowerCase()
      .match(/\b\w+\b/g) // Basic word tokenizer
      ?.filter((word) => !stopWords.has(word)) // Remove stop words
      .map((word) => stem(word)) || [] // Apply stemming
  ) // Return empty array if null
}

/**
 * Create a vector from tokens.
 *
 * @param tokens - The tokens to create a vector from
 * @param vocabulary - The vocabulary to create a vector from
 * @returns The vector
 */
function createVector(tokens: string[], vocabulary: string[]): number[] {
  return vocabulary.map((word) => tokens.filter((t) => t === word).length)
}

/**
 * Calculate cosine similarity between two texts.
 *
 * @param tokens1 - The tokens of the first text
 * @param tokens2 - The tokens of the second text
 * @returns The cosine similarity between the two texts
 */
function compareTexts(tokens1: string[], tokens2: string[]): number {
  const vocabulary = Array.from(new Set([...tokens1, ...tokens2]))

  const vector1 = createVector(tokens1, vocabulary)
  const vector2 = createVector(tokens2, vocabulary)

  const similarity = cosineSimilarity(vector1, vector2)

  return similarity
}

/**
 * Find the most similar saved reply to a comment.
 *
 * @param commentText - The text of the comment
 * @param savedReplies - The saved replies
 * @returns The most similar saved reply  or null if no similar saved reply is found
 */
export function findMostSimilarSavedReply({
  commentText,
  savedReplies,
}: {
  commentText: string
  savedReplies: SimilaritySavedReply[]
}): SimilaritySavedReply | null {
  let highestSimilarity = -1
  let mostSimilarSavedReply = null

  if (commentText.length === 0) {
    return null
  }

  /* Lemmatization process is expensive, so we do it only once for the comment text */
  const tokensComment = tokenizeAndStem(commentText)

  savedReplies.forEach((savedReply) => {
    const tokensSavedReply = tokenizeAndStem(savedReply.content.text)
    const similarity = compareTexts(tokensComment, tokensSavedReply)

    if (similarity > highestSimilarity) {
      highestSimilarity = similarity
      mostSimilarSavedReply = savedReply
    }
  })

  // modifying this threshold we could make the algorithm more or less strict
  return highestSimilarity > 0.35 ? mostSimilarSavedReply : null
}
